// =====================================================================
//  ADMIN CONSOLE, ACTIVITY LOG
// ---------------------------------------------------------------------
//  The audit trail: who did what, when, and what changed.
//
//  This page used to carry a notice explaining that it was NOT an audit
//  trail, it was assembled from the `created_at` of business rows, so it
//  could only ever show that something appeared, never who made it appear,
//  and never an edit or a deletion at all.
//
//  There is now a real append-only `audit_logs` table behind it
//  (migration 0005), so the page shows actor, action, outcome and a
//  field-level before/after diff. Rows are immutable at the database
//  level, not by convention.
//
//  The diff is collapsed by default and expanded per entry: most of the
//  time the one-line summary is what you want, and opening every diff at
//  once would make the timeline unreadable.
// =====================================================================

import React, { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  Activity,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  FileText,
  History,
  LifeBuoy,
  LogIn,
  RefreshCw,
  Target,
  Trash2,
  UserPlus,
  Users,
  Wallet,
} from 'lucide-react';

import backendApi from '../../api/backendApi';
import {
  Button,
  Chip,
  EmptyState,
  FilterChip,
  LoadingBlock,
  Notice,
  PageHeader,
  Pager,
  Panel,
  PanelBody,
  PanelHead,
  SearchInput,
  StatusChip,
  Toolbar,
  ToolbarSpacer,
  useDebounced,
} from '../ui';
import { fmtDate, fmtDateTime, fmtNumber, matchesSearch, timeAgo, titleCase } from '../format';
// toneVar maps a tone key to the abbreviation used in the CSS variables
// (positive -> pos). Building the name directly from the tone key yields
// `--ac-positive-bg`, which does not exist and fails silently.
import { cssVar, toneVar } from '../theme';

// Keyed by the first segment of the dotted action ('employee.updated' ->
// 'employee'), which is how the server groups them.
const KIND_META = {
  employee: { icon: Users, label: 'Employee', tone: 'accent' },
  leave: { icon: CalendarDays, label: 'Leave', tone: 'info' },
  leaverequest: { icon: CalendarDays, label: 'Leave', tone: 'info' },
  ticket: { icon: LifeBuoy, label: 'Ticket', tone: 'warning' },
  document: { icon: FileText, label: 'Document', tone: 'neutral' },
  performance: { icon: Target, label: 'Review', tone: 'violet' },
  candidate: { icon: UserPlus, label: 'Candidate', tone: 'positive' },
  payroll: { icon: Wallet, label: 'Payroll', tone: 'positive' },
  auth: { icon: LogIn, label: 'Sign-in', tone: 'neutral' },
  user_deletion: { icon: Trash2, label: 'Account removal', tone: 'negative' },
};

const metaFor = (kind) =>
  KIND_META[kind] || { icon: Activity, label: titleCase(kind || 'Event'), tone: 'neutral' };

// The verb carries the meaning, so it gets its own colour.
const VERB_TONE = {
  created: 'positive',
  updated: 'info',
  deleted: 'negative',
  approved: 'positive',
  rejected: 'negative',
  failed: 'negative',
};

const verbOf = (action) => String(action || '').split('.').slice(1).join('.') || 'event';
const verbTone = (action) => {
  const verb = verbOf(action);
  return VERB_TONE[verb.split('.').pop()] || 'neutral';
};

const ACTION_FILTERS = [
  { value: 'employee', label: 'Employee' },
  { value: 'leave', label: 'Leave' },
  { value: 'leaverequest', label: 'Leave requests' },
  { value: 'payroll', label: 'Payroll' },
  { value: 'ticket', label: 'Tickets' },
  { value: 'document', label: 'Documents' },
  { value: 'performance', label: 'Reviews' },
  { value: 'candidate', label: 'Candidates' },
  { value: 'auth', label: 'Sign-ins' },
];

const OUTCOME_FILTERS = [
  { value: 'success', label: 'Successful' },
  { value: 'failure', label: 'Failed' },
];

const PAGE_SIZES = [50, 100, 200];

/** Renders {field: {from, to}} as a readable before/after list. */
function ChangeDiff({ changes }) {
  const entries = Object.entries(changes || {});
  if (!entries.length) return null;

  return (
    <div
      style={{
        marginTop: 8,
        borderLeft: `2px solid ${cssVar.line}`,
        paddingLeft: 10,
        display: 'grid',
        gap: 4,
      }}
    >
      {entries.map(([field, value]) => {
        // A diff entry is {from, to}; anything else is free-form context
        // (a deleted row snapshot, a rejection comment).
        const isDiff = value && typeof value === 'object' && ('from' in value || 'to' in value);

        return (
          <div key={field} style={{ fontSize: 12, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ color: cssVar.inkMuted, minWidth: 120 }}>{titleCase(field)}</span>
            {isDiff ? (
              <>
                <span
                  style={{
                    color: cssVar.inkFaint,
                    textDecoration: 'line-through',
                    wordBreak: 'break-word',
                  }}
                >
                  {value.from === null || value.from === '' ? ',' : String(value.from)}
                </span>
                <span style={{ color: cssVar.inkFaint }}>→</span>
                <span style={{ color: cssVar.inkStrong, wordBreak: 'break-word' }}>
                  {value.to === null || value.to === '' ? ',' : String(value.to)}
                </span>
              </>
            ) : (
              <span className="ac-mono" style={{ color: cssVar.inkBase, wordBreak: 'break-word' }}>
                {typeof value === 'object' ? JSON.stringify(value) : String(value)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function ActivityLogPage() {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [actionFilter, setActionFilter] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());

  const debouncedSearch = useDebounced(search, 250);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['admin', 'audit', actionFilter, outcomeFilter, page, pageSize],
    queryFn: () =>
      backendApi.admin.audit({
        action: actionFilter || undefined,
        outcome: outcomeFilter || undefined,
        limit: pageSize,
        offset: page * pageSize,
      }),
    placeholderData: keepPreviousData,
  });

  // Memoised rather than `data?.rows || []`, because a fresh [] on every
  // render would invalidate the filter/group memos below on every render and
  // defeat them entirely.
  const entries = useMemo(() => data?.rows ?? [], [data]);
  const total = data?.total || 0;

  // Search is client-side over the current page only. Action, outcome and
  // paging are server-side; free-text is a refinement of what is on screen,
  // and the toolbar says so rather than implying a full-corpus search.
  const filtered = useMemo(
    () =>
      entries.filter((entry) =>
        matchesSearch(entry, debouncedSearch, [
          'summary',
          'entity_label',
          'actor_name',
          'actor_email',
          'action',
        ])
      ),
    [entries, debouncedSearch]
  );

  const grouped = useMemo(() => {
    const groups = new Map();
    filtered.forEach((entry) => {
      const day = String(entry.created_at || '').slice(0, 10);
      if (!groups.has(day)) groups.set(day, []);
      groups.get(day).push(entry);
    });
    return Array.from(groups.entries());
  }, [filtered]);

  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const resetPage = () => setPage(0);
  const filtersActive = Boolean(actionFilter || outcomeFilter || search);

  // Entries created before the audit table existed are reconstructed from
  // `created_at` and flagged by the server, so the page can be honest about
  // which rows are real audit records.
  const hasSynthetic = entries.some((entry) => entry.audited === false);

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="ac-page">
      <PageHeader
        title="Activity Log"
        sub="Every recorded change, who made it, and what it changed."
        actions={
          <Button icon={RefreshCw} onClick={() => refetch()} loading={isFetching}>
            Refresh
          </Button>
        }
      />

      {hasSynthetic ? (
        <div style={{ marginBottom: 14 }}>
          <Notice tone="warning" title="Some entries predate the audit log">
            Older events are reconstructed from when records were created, so they
            show no actor and no before/after detail. Everything from now on is
            recorded in full.
          </Notice>
        </div>
      ) : null}

      <Panel>
        <PanelHead
          title="Timeline"
          sub={`${fmtNumber(total)} recorded event${total === 1 ? '' : 's'}`}
        />

        <Toolbar>
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search this page…"
            width={230}
          />
          <FilterChip
            label="Area"
            value={actionFilter}
            options={ACTION_FILTERS}
            onChange={(next) => {
              setActionFilter(next);
              resetPage();
            }}
          />
          <FilterChip
            label="Outcome"
            value={outcomeFilter}
            options={OUTCOME_FILTERS}
            onChange={(next) => {
              setOutcomeFilter(next);
              resetPage();
            }}
          />
          {filtersActive ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setSearch('');
                setActionFilter('');
                setOutcomeFilter('');
                resetPage();
              }}
            >
              Clear
            </Button>
          ) : null}
          <ToolbarSpacer />
          {isFetching ? <span className="ac-spinner" /> : null}
        </Toolbar>

        <PanelBody flush>
          {isLoading ? (
            <LoadingBlock label="Loading activity…" height={240} />
          ) : grouped.length === 0 ? (
            <EmptyState
              icon={History}
              title={filtersActive ? 'No matching activity' : 'No activity recorded yet'}
              text={
                filtersActive
                  ? 'Try a different search term or clear the filters.'
                  : 'Changes made in the HRMS, edits, approvals, sign-ins, will appear here with the person who made them.'
              }
            />
          ) : (
            grouped.map(([day, items]) => (
              <div key={day}>
                <div
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 14px',
                    background: cssVar.sunken,
                    borderBottom: `1px solid ${cssVar.lineSoft}`,
                    borderTop: `1px solid ${cssVar.lineSoft}`,
                  }}
                >
                  <span className="ac-eyebrow">{day === today ? 'Today' : fmtDate(day)}</span>
                  <span style={{ fontSize: 10.5, color: cssVar.inkFaint }}>
                    {items.length} event{items.length === 1 ? '' : 's'}
                  </span>
                </div>

                {items.map((entry) => {
                  const kind = String(entry.action || entry.kind || '').split('.')[0];
                  const meta = metaFor(kind);
                  const hasChanges = Object.keys(entry.changes || {}).length > 0;
                  const isOpen = expanded.has(entry.id);

                  return (
                    <div className="ac-feed-item" key={entry.id} style={{ alignItems: 'flex-start' }}>
                      <span
                        className="ac-feed-icon"
                        style={{
                          background: `var(--ac-${toneVar(meta.tone)}-bg)`,
                          borderColor: `var(--ac-${toneVar(meta.tone)}-bd)`,
                          color: `var(--ac-${toneVar(meta.tone)}-fg)`,
                        }}
                      >
                        <meta.icon size={13} strokeWidth={2} />
                      </span>

                      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                        <div
                          style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}
                        >
                          <span className="ac-feed-title">
                            {entry.summary || entry.entity_label || titleCase(kind)}
                          </span>
                          <Chip tone={meta.tone}>{meta.label}</Chip>
                          {entry.action ? (
                            <Chip tone={verbTone(entry.action)}>{titleCase(verbOf(entry.action))}</Chip>
                          ) : null}
                          {entry.outcome === 'failure' ? (
                            <StatusChip value="Failed" dot={false} />
                          ) : null}
                        </div>

                        <div className="ac-feed-meta">
                          {/* The actor is the whole point of an audit entry,
                              so it leads. Falls back to the attempted email
                              for failed sign-ins, which have no account. */}
                          {entry.actor_name || entry.actor_email || 'System'}
                          {entry.actor_role ? ` · ${titleCase(entry.actor_role)}` : ''}
                          {' · '}
                          {fmtDateTime(entry.created_at)}
                          {entry.ip_address ? ` · ${entry.ip_address}` : ''}
                        </div>

                        {hasChanges ? (
                          <>
                            <button
                              type="button"
                              onClick={() => toggle(entry.id)}
                              aria-expanded={isOpen}
                              style={{
                                marginTop: 6,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                cursor: 'pointer',
                                font: 'inherit',
                                fontSize: 11.5,
                                color: cssVar.accent,
                              }}
                            >
                              {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                              {isOpen ? 'Hide detail' : `Show detail (${Object.keys(entry.changes).length})`}
                            </button>
                            {isOpen ? <ChangeDiff changes={entry.changes} /> : null}
                          </>
                        ) : null}
                      </div>

                      <span style={{ fontSize: 11, color: cssVar.inkFaint, flex: '0 0 auto' }}>
                        {timeAgo(entry.created_at)}
                      </span>
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </PanelBody>

        {total > pageSize ? (
          <div style={{ borderTop: `1px solid ${cssVar.lineSoft}`, padding: '8px 14px' }}>
            <Pager
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                resetPage();
              }}
              sizes={PAGE_SIZES}
            />
          </div>
        ) : null}
      </Panel>
    </div>
  );
}
